import { useCallback, useDeferredValue, useEffect, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LinkIcon } from '@utils/icons';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { StatusGlyph } from './StatusGlyph';
import { GenerationSelect } from './GenerationSelect';
import { useToast } from '@components/ui/Toasts/toastContext';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { SortSelect } from './SortSelect';
import { MatrixTable } from './MatrixTable';
import { useProgressMatrix } from './useProgressMatrix';
import { useTrackerLayoutHold } from './useTrackerLayoutHold';
import type { ITrackerPopulation } from './useTrackerPopulation';
import { FilterNoMatches } from '@components/ui/FilterNoMatches/FilterNoMatches';
import { useTrackerAreaEditor } from './useTrackerAreaEditor';
import type { ITrackerWriteFailure } from './trackerWriteFailure';
import { readTrackerPrefs, writeTrackerPrefs } from './trackerPrefs';
import type { ColumnSort, SortDir } from './progressMatrix';
import { useDeferredReady } from '@hooks/useDeferredReady';
import { useOpenTaskPanel } from '../hooks/useActivityPanel';
import { ViewLoadingCard } from '../ViewLoading/ViewLoadingCard';
import styles from './progressTracker.module.css';

export interface IProgressTrackerProps {
  siteId: string;
  /** The tracker's activities and its "hide 100%" toggle, held by the PAGE
   *  (`useTrackerPopulation`) — the header counts the same set the columns
   *  render, chosen by the selector the Daily List keeps its activities by. */
  population: ITrackerPopulation;
  /** The page's activities are not in yet (or the site's zone is unresolved). */
  loading: boolean;
}

/**
 * Deferred first paint: the outer component's first commit is just the
 * loading card, while the real body (CPM solve + matrix build + grid mount)
 * renders at transition priority and appears whole.
 */
export function ProgressTracker(props: IProgressTrackerProps) {
  const ready = useDeferredReady();
  if (!ready) return <TrackerLoadingCard />;
  return <TrackerBody {...props} />;
}

/** Both loading phases — the deferred-first-paint gate above and the
 *  schedule-fetch gate in TrackerBody — render the one shared card. */
function TrackerLoadingCard() {
  const { t } = useTranslation();
  return <ViewLoadingCard label={t('activities.tracker.loading')} className={styles.card} />;
}

function TrackerBody({ siteId, population, loading }: IProgressTrackerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Restore the last-used level/sort/direction for this site (persisted to
  // localStorage) so leaving and returning to Activities doesn't reset them.
  const [generation, setGeneration] = useState(() => readTrackerPrefs(siteId).generation ?? 1);
  const [sort, setSort] = useState<ColumnSort>(() => readTrackerPrefs(siteId).sort ?? 'default');
  const [dir, setDir] = useState<SortDir>(() => readTrackerPrefs(siteId).dir ?? 'desc');
  const { hideDone, setHideDone } = population;
  // buildProgressMatrix clamps a level that exceeds this schedule's depth to the
  // deepest available, so a restored level that's too deep for a different
  // schedule just shows Sub-tasks/Activities rather than an empty grid.
  // Search dispatches per keystroke WITHOUT a transition (the input must stay
  // controlled-synchronous) — defer the population it narrows so typing never
  // waits on a full matrix rebuild at hundreds of columns; the grid catches up
  // a frame later.
  const shown = useDeferredValue(population.activities);
  const { matrix: built, scheduleNumber, taskGenerations } = useProgressMatrix(siteId, generation, sort, dir, shown);
  // While the user works the grid its columns stay put (QA F-61).
  const { matrix, zoneRef, begin } = useTrackerLayoutHold(population, built, `${generation}:${sort}:${dir}`);
  const maxGeneration = Math.max(1, matrix.maxGeneration);

  // Persist the settings whenever they change.
  useEffect(() => {
    writeTrackerPrefs(siteId, { generation, sort, dir, hideDone });
  }, [siteId, generation, sort, dir, hideDone]);

  const [isRelayoutPending, startRelayout] = useTransition();
  const changeGeneration = useCallback((g: number) => startRelayout(() => setGeneration(g)), []);
  const changeSort = useCallback((s: ColumnSort) => startRelayout(() => setSort(s)), []);
  const changeDir = useCallback((d: SortDir) => startRelayout(() => setDir(d)), []);
  const changeHideDone = useCallback((v: boolean) => startRelayout(() => setHideDone(v)), [setHideDone]);

  // Dependency jump to a task of ANOTHER generation: switch levels, pulse the
  // level select so the silent switch is noticed, and leave the scroll+flash
  // pending — MatrixTable runs it once the new generation's matrix lands and
  // the target actually is a column.
  const [pendingJump, setPendingJump] = useState<{ taskId: string } | null>(null);
  const [generationFlash, setGenerationFlash] = useState(0);
  const pushToast = useToast();
  // An unlocated jump target (no column at ANY generation) offers the Scheduler
  // instead — behind a confirmation, since it navigates away from this page.
  const [schedulerPrompt, setSchedulerPrompt] = useState<{ taskId: string; taskName: string } | null>(null);
  const jumpToOtherGeneration = useCallback(
    (taskId: string, taskName: string) => {
      const targetGeneration = taskGenerations.get(taskId);
      if (!targetGeneration) {
        setSchedulerPrompt({ taskId, taskName });
        return;
      }
      if (targetGeneration === generation) {
        // It IS this generation's task - just hidden by the active filters.
        pushToast({ kind: 'error', title: t('activities.tracker.deps.jumpFiltered') });
        return;
      }
      startRelayout(() => setGeneration(targetGeneration));
      setGenerationFlash((n) => n + 1);
      setPendingJump({ taskId });
    },
    [taskGenerations, generation, pushToast, t],
  );
  const clearPendingJump = useCallback(() => setPendingJump(null), []);
  // A pending jump whose task never became a column (the target generation's
  // filters exclude it) must not linger and fire on a later filter change —
  // drop it once the relayout transition has settled without it.
  useEffect(() => {
    if (pendingJump && !isRelayoutPending && !matrix.columns.some((c) => c.taskId === pendingJump.taskId)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one bounded cleanup, fired at most once per dropped jump when the relayout transition settles without the target column
      setPendingJump(null);
      pushToast({ kind: 'error', title: t('activities.tracker.deps.jumpFiltered') });
    }
  }, [pendingJump, isRelayoutPending, matrix, pushToast, t]);

  // Clicking an activity header opens the shared scheduler TaskPanel drawer
  // (the same one the Scheduler shows on task click). ActivitiesPage bootstraps
  // the scheduler store and mounts the panel; here we just open it by task id.
  const openActivity = useOpenTaskPanel();

  // "Show in scheduler" — deep-link into the Scheduler focused on the task, the
  // same target the Dashboard's Critical Path card uses (`?focusTask=<id>`).
  const showInScheduler = useCallback(
    (taskId: string) => {
      if (!siteId) return;
      const base = scheduleNumber
        ? `/scheduler/${encodeURIComponent(siteId)}/${encodeURIComponent(scheduleNumber)}`
        : `/scheduler/${encodeURIComponent(siteId)}`;
      navigate(`${base}?focusTask=${encodeURIComponent(taskId)}`);
    },
    [navigate, siteId, scheduleNumber],
  );

  // Click an area cell to report on it (task columns) — persists + re-blends
  // the task % just like the Scheduler's area board; a manager's click on an
  // area awaiting review opens the review instead.
  const onWriteFailed = useCallback(
    (copy: ITrackerWriteFailure) => pushToast({ kind: 'error', title: t(copy.title), sub: t(copy.sub) }),
    [pushToast, t],
  );
  const { areaEditor, review } = useTrackerAreaEditor(siteId, scheduleNumber, matrix.columns, onWriteFailed);

  const totalLocated = matrix.columns.length;

  // First load: the schedule detail arrives via a list→detail waterfall, so show a
  // lightweight placeholder (like the Scheduler) instead of a blank/empty grid that
  // pops to content. `loading` is only true before the first payload lands, not on
  // background refetches, so the built matrix never flashes this.
  if (loading) return <TrackerLoadingCard />;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.title}>
            {t('activities.tracker.title')}
            {matrix.buildingName ? ` · ${matrix.buildingName}` : ''}
          </span>
          {/* Keyed by the flash nonce: a cross-generation dependency jump
              remounts the wrapper, replaying the pulse that flags the level
              switch. Nonce 0 (never jumped) renders no animation class. */}
          <span key={generationFlash} className={generationFlash > 0 ? styles.generationFlash : undefined}>
            <GenerationSelect value={Math.min(generation, maxGeneration)} max={maxGeneration} onChange={changeGeneration} />
          </span>
          <SortSelect value={sort} onChange={changeSort} dir={dir} onDirChange={changeDir} />
        </div>
        <div className={styles.legend}>
          <label className={styles.hideDone}>
            <Checkbox
              checked={hideDone}
              onCheckedChange={changeHideDone}
              ariaLabel={t('activities.tracker.hideDone')}
            />
            {t('activities.tracker.hideDone')}
          </label>
          <span className={`${styles.legendGlyph} ${styles.legendGlyphPlanned}`}><StatusGlyph status="planned" size={13} /></span>{t('activities.tracker.legend.planned')}
          <span className={`${styles.legendGlyph} ${styles.legendGlyphProgress}`}><StatusGlyph status="inProgress" size={13} /></span>{t('activities.tracker.legend.inProgress')}
          <span className={`${styles.legendGlyph} ${styles.legendGlyphDone}`}><StatusGlyph status="complete" size={13} /></span>{t('activities.tracker.legend.completed')}
          <span className={styles.coupledIcon}><LinkIcon size={15} /></span>{t('activities.tracker.legend.coupled')}
        </div>
      </div>
      {totalLocated === 0 && matrix.maxGeneration <= 1 ? (
        // Activities with areas the filters all leave out are a filter miss,
        // with the way back; none at all is the schedule's state.
        population.hasLocated ? (
          <FilterNoMatches onClear={population.clearFilters} />
        ) : (
          <div className={styles.empty}>{t('activities.tracker.empty')}</div>
        )
      ) : (
        <div ref={zoneRef} className={styles.holdZone} onPointerDownCapture={begin} onKeyDownCapture={begin}>
          <MatrixTable
            matrix={matrix}
            onOpenActivity={openActivity}
            onShowInScheduler={showInScheduler}
            areaEditor={areaEditor}
            onJumpMissing={jumpToOtherGeneration}
            pendingJumpTaskId={pendingJump?.taskId ?? null}
            onPendingJumpDone={clearPendingJump}
          />
        </div>
      )}
      {review}
      <Modal
        open={schedulerPrompt !== null}
        onClose={() => setSchedulerPrompt(null)}
        title={t('activities.tracker.deps.schedulerPromptTitle')}
        description={schedulerPrompt ? t('activities.tracker.deps.schedulerPromptBody', { name: schedulerPrompt.taskName }) : undefined}
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setSchedulerPrompt(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (schedulerPrompt) showInScheduler(schedulerPrompt.taskId);
                setSchedulerPrompt(null);
              }}
            >
              {t('activities.tracker.deps.schedulerPromptConfirm')}
            </Button>
          </>
        }
      />
    </div>
  );
}
